import React, { useState } from 'react';
import { X, Loader2, ImagePlus, Paperclip, Trash2, Send } from 'lucide-react';
import type { TransparencyProject, TransparencyProjectStatus } from '@yawi/types';
import type { UploadedFile } from '@yawi/types';
import { useServices, useToast } from '@/app/providers';
import { useFileUpload } from '@/shared/hooks';
import { Button } from '../../../../presentation/components/atoms/Button';
import { Input } from '../../../../presentation/components/atoms/Input';

const STATUS_LABEL: Record<string, string> = {
  PLANNING: 'Planeación',
  APPROVED: 'Aprobado',
  IN_PROGRESS: 'En progreso',
  PAUSED: 'Pausado',
  DELAYED: 'Retrasado',
  COMPLETED: 'Completado',
  CANCELLED: 'Cancelado',
};

interface AddUpdateModalProps {
  project: TransparencyProject;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * La acción más usada del módulo: el admin no edita el proyecto, publica una
 * entrada de bitácora. El avance y el estado del proyecto solo se mueven desde
 * acá, así que ningún cambio queda sin explicación para el residente.
 */
export const AddUpdateModal: React.FC<AddUpdateModalProps> = ({ project, onClose, onSaved }) => {
  const { transparencyProjectRepo } = useServices();
  const toast = useToast();

  const imageUpload = useFileUpload('transparency_images');
  const documentUpload = useFileUpload('transparency_documents');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [progressChange, setProgressChange] = useState('');
  const [statusChange, setStatusChange] = useState<TransparencyProjectStatus | ''>('');
  const [images, setImages] = useState<UploadedFile[]>([]);
  const [documents, setDocuments] = useState<UploadedFile[]>([]);
  const [saving, setSaving] = useState(false);

  const handlePickImages = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    // Secuencial a propósito: useFileUpload comparte estado de progreso, y en
    // paralelo el indicador "Subiendo..." se pisaría entre archivos.
    for (const file of Array.from(fileList)) {
      const uploaded = await imageUpload.upload(file);
      if (uploaded) setImages((prev) => [...prev, uploaded]);
    }
  };

  const handlePickDocuments = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    for (const file of Array.from(fileList)) {
      const uploaded = await documentUpload.upload(file);
      if (uploaded) setDocuments((prev) => [...prev, uploaded]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    try {
      await transparencyProjectRepo.addTimelineUpdate(project.id, {
        title: title.trim(),
        description: description.trim() || undefined,
        progressChange: progressChange === '' ? undefined : Number(progressChange),
        statusChange: statusChange || undefined,
        imageFileIds: images.map((i) => i.id),
        documentFileIds: documents.map((d) => d.id),
      });
      toast.success('Actualización publicada');
      onSaved();
      onClose();
    } catch {
      toast.error('No se pudo publicar la actualización');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">Nueva actualización</h3>
            <p className="text-xs text-slate-500">{project.title}</p>
          </div>
          <button onClick={onClose} type="button">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
          <Input
            label="¿Qué pasó?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej. Se colocaron los nuevos azulejos"
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Detalle (opcional)
            </label>
            <textarea
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Cuenta el avance con más detalle para los residentes..."
            />
          </div>

          {/* Fotos */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Fotos</label>
            <div className="flex flex-wrap gap-2">
              {images.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.url}
                    alt={img.originalName}
                    className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                  />
                  <button
                    type="button"
                    onClick={() => setImages((prev) => prev.filter((i) => i.id !== img.id))}
                    className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-1 text-slate-400 hover:text-red-500 shadow-sm"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <label
                className={`w-20 h-20 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary-400 hover:text-primary-500 cursor-pointer transition-colors ${
                  imageUpload.isUploading ? 'opacity-50 cursor-wait' : ''
                }`}
              >
                {imageUpload.isUploading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <ImagePlus size={18} />
                )}
                <span className="text-[10px]">Agregar</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={imageUpload.isUploading}
                  onChange={(e) => handlePickImages(e.target.files)}
                />
              </label>
            </div>
          </div>

          {/* Documentos */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Documentos</label>
            {documents.length > 0 && (
              <ul className="mb-2 space-y-1">
                {documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 rounded-lg text-sm"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Paperclip size={14} className="text-slate-400 shrink-0" />
                      <span className="truncate text-slate-700">{doc.originalName}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setDocuments((prev) => prev.filter((d) => d.id !== doc.id))}
                      className="text-slate-300 hover:text-red-500 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label
              className={`inline-flex items-center gap-2 px-4 py-2 border border-slate-300 shadow-sm text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 cursor-pointer transition-colors ${
                documentUpload.isUploading ? 'opacity-50 cursor-wait' : ''
              }`}
            >
              {documentUpload.isUploading ? (
                <Loader2 size={16} className="animate-spin text-slate-500" />
              ) : (
                <Paperclip size={16} className="text-slate-500" />
              )}
              Adjuntar documento
              <input
                type="file"
                multiple
                className="hidden"
                disabled={documentUpload.isUploading}
                onChange={(e) => handlePickDocuments(e.target.files)}
              />
            </label>
          </div>

          <hr className="border-slate-100" />

          <p className="text-xs text-slate-500">
            Opcional: si esta actualización cambia el avance o el estado del proyecto, indicalo acá.
            Es la única forma de moverlos.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nuevo % de avance"
              type="number"
              min={0}
              max={100}
              value={progressChange}
              onChange={(e) => setProgressChange(e.target.value)}
              placeholder={`Actual: ${project.progress}%`}
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nuevo estado</label>
              <select
                value={statusChange}
                onChange={(e) => setStatusChange(e.target.value as TransparencyProjectStatus | '')}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Sin cambio</option>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={saving || imageUpload.isUploading || documentUpload.isUploading}
              icon={saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            >
              Publicar actualización
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
